import { motion } from "framer-motion";
import { Phone, Mail, Clock, MapPin, ShieldCheck, Facebook, Instagram } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import QuoteForm from "@/components/QuoteForm";
import Seo from "@/components/Seo";
import NJMap from "@/components/NJMap";
import { SITE, COUNTIES } from "@/data/site";

const CONTACT_ITEMS = [
  { icon: Phone, label: "Call Us Directly", value: SITE.phoneDisplay, href: SITE.phoneHref, testid: "contact-phone" },
  { icon: Mail, label: "Email Us", value: SITE.email, href: SITE.emailHref, testid: "contact-email" },
  { icon: Clock, label: "Operating Hours", value: SITE.hoursLong, testid: "contact-hours" },
  { icon: MapPin, label: "Based In", value: `${SITE.baseCity} · Serving all of New Jersey`, testid: "contact-location" },
];

export default function Contact() {
  return (
    <>
      {/* Header */}
      <Seo
        title="Contact Ardeca Construction | Free Quote in Freehold, NJ"
        description="Contact Ardeca Construction for a fast, no-pressure remodeling quote in Freehold, NJ. Licensed, insured contractors serving homeowners across New Jersey."
        url="/contact"
        image="/IMG-20260606-WA0018.jpg"
      />
      <section
        data-testid="contact-header"
        className="relative overflow-hidden text-white"
        style={{
          backgroundImage: 'url("/renovations in monmouth county new jersey.webp")',
          backgroundSize: "cover",
          backgroundPosition: "center center",
        }}
      >
        <div className="absolute inset-0 bg-black/60" />
        <div className="relative container-x py-16 md:py-24 text-center">
          <motion.p initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/20 px-4 py-2 text-xs font-semibold text-white">
            <ShieldCheck size={15} className="text-brand-orange" /> Free, No-Pressure Estimates
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.08 }}
            className="mt-6 font-extrabold tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
            Let's Fix What's Bugging<br />You About Your Home
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.16 }}
            className="mt-6 text-lg text-white/75 max-w-2xl mx-auto leading-relaxed">
            Tell us about your project and a member of the Ardeca family will reach out — usually the same day.
            Or call us directly, we're happy to talk it through.
          </motion.p>
        </div>
      </section>

      {/* Two columns */}
      <section className="container-x py-16 md:py-24">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          {/* Left: info + map */}
          <Reveal>
            <p className="eyebrow">Get In Touch</p>
            <h2 className="h2 mt-4">We're Local, Friendly & Ready to Help</h2>
            <p className="lead mt-5">Proudly based in {SITE.baseCity}, serving {SITE.county} and homeowners across the entire Garden State.</p>

            <div className="mt-8 space-y-4">
              {CONTACT_ITEMS.map((c) => {
                const Icon = c.icon;
                const inner = (
                  <div className="flex items-start gap-4 rounded-2xl bg-white border border-brand-cream p-5 shadow-sm transition-colors hover:border-brand-orange">
                    <span className="grid place-items-center h-12 w-12 shrink-0 rounded-xl bg-brand-orange/10 text-brand-orange"><Icon size={20} /></span>
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-[0.14em] text-brand-muted">{c.label}</p>
                      <p className="mt-1 font-semibold text-brand-dark break-words">{c.value}</p>
                    </div>
                  </div>
                );
                return c.href ? (
                  <a key={c.testid} href={c.href} data-testid={c.testid} className="block">{inner}</a>
                ) : (
                  <div key={c.testid} data-testid={c.testid}>{inner}</div>
                );
              })}
            </div>

            {/* Social media */}
            <div className="mt-8">
              <h3 className="text-sm font-semibold uppercase tracking-[0.14em] text-brand-muted mb-3">Follow Us</h3>
              <div className="flex items-center gap-3">
                <a
                  href={SITE.facebook}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="contact-facebook"
                  aria-label="Ardeca Construction on Facebook"
                  className="inline-flex items-center gap-2 rounded-xl bg-white border border-brand-cream px-4 py-2.5 text-sm font-semibold text-brand-dark shadow-sm transition-colors hover:border-brand-orange hover:text-brand-orange"
                >
                  <Facebook size={18} className="text-brand-orange" /> Facebook
                </a>
                <a
                  href={SITE.instagram}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="contact-instagram"
                  aria-label="Ardeca Construction on Instagram"
                  className="inline-flex items-center gap-2 rounded-xl bg-white border border-brand-cream px-4 py-2.5 text-sm font-semibold text-brand-dark shadow-sm transition-colors hover:border-brand-orange hover:text-brand-orange"
                >
                  <Instagram size={18} className="text-brand-orange" /> Instagram
                </a>
              </div>
            </div>

            {/* Coverage map */}
            <div className="mt-8">
              <h3 className="text-sm font-semibold uppercase tracking-[0.14em] text-brand-muted mb-3">Our New Jersey Service Area</h3>
              <NJMap height={320} />
              <div className="mt-3 flex items-center gap-3">
                <span className="inline-flex items-center gap-2 text-sm text-brand-muted">
                  <span className="inline-block h-3 w-3 rounded-full bg-[#ef4444] border" />
                  Freehold (Ardeca)
                </span>
                <span className="inline-flex items-center gap-2 text-sm text-brand-muted">
                  <span className="inline-block h-3 w-6 rounded bg-[#fb923c] opacity-30 border" />
                  Service area: All New Jersey counties
                </span>
              </div>
              <div className="mt-6">
                <p className="text-sm font-semibold text-brand-dark mb-3">Counties of New Jersey</p>
                <div className="flex flex-wrap gap-2">
                  {COUNTIES.map((c) => (
                    <span key={c} className="rounded-full bg-brand-cream/70 border border-brand-border px-3 py-1.5 text-xs font-medium text-brand-dark">{c}</span>
                  ))}
                </div>
              </div>
            </div>
          </Reveal>

          {/* Right: form */}
          <Reveal delay={0.1}>
            <div id="quote" className="rounded-[28px] bg-brand-creamSoft border border-brand-cream p-6 sm:p-8 md:p-10 shadow-sm">
              <h2 className="text-2xl md:text-3xl font-extrabold text-brand-dark">Request Your Free Quote</h2>
              <p className="mt-2 text-brand-muted">Fill out the details below and we'll be in touch shortly.</p>
              <div className="mt-7">
                <QuoteForm />
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
